import { useCallback, useEffect, useMemo } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { HttpStatusCode } from 'axios';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { QueryKeys } from '../enums/queryKeys';
import { getApiErrorMessage, getApiErrorStatus } from '../shared/utils/apiErrorMessage';
import InfrastructureManagerService from '../services/InfrastructureManager/infrastructuremanager.service';
import { infrastructureServiceMessages } from '../services/InfrastructureManager/infrastructure.constants';
import type { ContactGroupOption } from '../types/InfrastructureManagerTypes';

const MESSAGES = infrastructureServiceMessages.CONTACTS;

const NO_GROUP_OPTIONS: ContactGroupOption[] = [];

// A 409 (duplicate name/email, Ungrouped rules) is correctable input, so it warns rather than errors.
const notifyContactError = (title: string, error: unknown) =>
  Notification({
    message: title,
    description: getApiErrorMessage(error, (error as Error).message),
    type: getApiErrorStatus(error) === HttpStatusCode.Conflict ? NotificationTypes.WARNING : NotificationTypes.ERROR,
  });

const useContactMutation = <TVariables>(options: UseMutationOptions<void, Error, TVariables>, successMessage: string, errorMessage: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    // Returned so mutateAsync settles once the refreshed list is in the cache.
    onSuccess: () => {
      Notification({ message: successMessage, type: NotificationTypes.SUCCESS });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_CONTACT_GROUPS] }),
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_CONTACT_GROUP_OPTIONS] }),
      ]);
    },
    onError: (error) => notifyContactError(errorMessage, error),
  });
};

export const useContacts = (search: string) => {
  const service = InfrastructureManagerService();

  const { data, error, isLoading, isLoadingError, isFetchNextPageError, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery(
    service.getContactGroups(search),
  );

  useEffect(() => {
    if (!error) return;
    notifyContactError(isFetchNextPageError ? MESSAGES.LOAD_MORE_ERROR : MESSAGES.GET_ERROR, error);
  }, [error, isFetchNextPageError]);

  const groups = useMemo(() => data?.pages.flatMap((page) => page.groups) ?? [], [data]);
  const loadMore = useCallback(() => void fetchNextPage(), [fetchNextPage]);

  const createGroup = useContactMutation(service.createContactGroup(), MESSAGES.CREATE_GROUP_SUCCESS, MESSAGES.CREATE_GROUP_ERROR);
  const renameGroup = useContactMutation(service.renameContactGroup(), MESSAGES.RENAME_GROUP_SUCCESS, MESSAGES.RENAME_GROUP_ERROR);
  const deleteGroup = useContactMutation(service.deleteContactGroup(), MESSAGES.DELETE_GROUP_SUCCESS, MESSAGES.DELETE_GROUP_ERROR);
  const createContact = useContactMutation(service.createContact(), MESSAGES.CREATE_SUCCESS, MESSAGES.CREATE_ERROR);
  const updateContact = useContactMutation(service.updateContact(), MESSAGES.UPDATE_SUCCESS, MESSAGES.UPDATE_ERROR);
  const duplicateContact = useContactMutation(service.duplicateContact(), MESSAGES.DUPLICATE_SUCCESS, MESSAGES.DUPLICATE_ERROR);
  const moveContact = useContactMutation(service.moveContact(), MESSAGES.MOVE_SUCCESS, MESSAGES.MOVE_ERROR);
  const deleteContact = useContactMutation(service.deleteContact(), MESSAGES.DELETE_SUCCESS, MESSAGES.DELETE_ERROR);

  return {
    groups,
    isLoadingContacts: isLoading,
    isContactsLoadingError: isLoadingError,
    isFetchingNextPage,
    // A failed page stays failed until the list refetches; re-observing would retry it in a loop.
    canLoadMore: hasNextPage && !isFetchingNextPage && !isFetchNextPageError,
    loadMore,
    createGroup: createGroup.mutateAsync,
    renameGroup: renameGroup.mutateAsync,
    deleteGroup: deleteGroup.mutateAsync,
    createContact: createContact.mutateAsync,
    updateContact: updateContact.mutateAsync,
    duplicateContact: duplicateContact.mutateAsync,
    moveContact: moveContact.mutateAsync,
    deleteContact: deleteContact.mutateAsync,
    isCreatingGroup: createGroup.isPending,
    isSavingContact: createContact.isPending || updateContact.isPending,
    isDeleting: deleteGroup.isPending || deleteContact.isPending,
  };
};

export const useContactGroupOptions = (enabled = true) => {
  const { getContactGroupOptions } = InfrastructureManagerService();
  const { data, error } = useQuery({ ...getContactGroupOptions(), enabled });

  useEffect(() => {
    if (error) notifyContactError(MESSAGES.GROUP_OPTIONS_ERROR, error);
  }, [error]);

  return data ?? NO_GROUP_OPTIONS;
};
